import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import _ from 'lodash';
import type { ComponentPropsWithoutRef, ReactNode, RefObject } from 'react';
import { Fragment, useEffect, useRef } from 'react';

import { HubTooltip, Tag } from '@adflow/frontend/ui-common';
import { theme } from '@adflow/frontend/ui-theme';

export interface UploadResultTag {
	avbId: string;
	type: 'success' | 'error';
	errors?: unknown[];
}

export interface UploadResultTagsCellProps {
	tags: readonly Readonly<UploadResultTag>[];
	textStyles?: CSSInterpolation;
}

const styles = {
	container: css({
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		width: '100%',
		height: '100%',
		gap: '0.25rem',
		lineHeight: 0,
		padding: '0.5rem 0',
	}),
	hiddenTagsTooltipTitle: css({
		fontWeight: 'bold',
		marginBottom: '0.5rem',
	}),
	hiddenTagsContainer: css({
		display: 'flex',
		flexWrap: 'wrap',
		gap: '0.25rem',
		maxWidth: '300px',
	}),
	truncatorIndicator: css({
		fontSize: '12px',
		color: theme.colors.text.secondary,
		fontWeight: 500,
		paddingLeft: '0.25rem',
		maxWidth: 24,
		cursor: 'help',
	}),
	errorTooltipContainer: css({
		maxHeight: '400px',
		overflow: 'auto',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	}),
	errorTooltipPre: css({
		margin: 0,
		fontSize: '12px',
		lineHeight: '1.4',
	}),
	errorTooltipList: css({
		paddingLeft: '1rem',
		listStyleType: 'decimal',
		li: {
			margin: '0.25rem 0',
		},
	}),
};

export function UploadResultTagsCell({
	tags,
	textStyles,
}: UploadResultTagsCellProps) {
	return (
		<TruncatedList
			css={styles.container}
			renderTruncator={({ hiddenItemsCount }) => {
				// Get the hidden tags for the tooltip
				const hiddenTags = tags.slice(tags.length - hiddenItemsCount);

				return (
					<HubTooltip
						title={
							<div>
								<div css={styles.hiddenTagsTooltipTitle}>
									Hidden ({hiddenItemsCount}):
								</div>
								<div css={styles.hiddenTagsContainer}>
									{hiddenTags.map((tag, index) => (
										<Tag
											key={`hidden-${tag.avbId}-${index}`}
											text={tag.avbId}
											size="tiny"
											color={
												tag.type === 'success'
													? theme.colors.statuses.ACTIVE
													: theme.colors.statuses.DELETED
											}
										/>
									))}
								</div>
							</div>
						}
						styles={{ tooltip: { maxWidth: 350 } }}
					>
						<div css={styles.truncatorIndicator}>+{hiddenItemsCount}</div>
					</HubTooltip>
				);
			}}
			truncatorWidth={24}
			items={[...tags].map((tag) => {
				if (tag.type === 'success') {
					return (
						<Tag
							textStyles={textStyles}
							key={`success-${tag.avbId}`}
							text={tag.avbId}
							size="tiny"
							color={theme.colors.statuses.ACTIVE}
						/>
					);
				} else {
					// Error tag with tooltip
					let errorTooltip: string;
					let isStringList = false;

					if (tag.errors && tag.errors.length > 0) {
						// Check if all errors are strings
						const allStrings = tag.errors.every(
							(error) => typeof error === 'string'
						);

						if (allStrings) {
							// Show string errors as a simple list
							errorTooltip = tag.errors.join('\n');
							isStringList = true;
						} else {
							// Show complex errors as JSON
							errorTooltip = JSON.stringify(tag.errors, null, 2);
							isStringList = false;
						}
					} else {
						errorTooltip = 'No error details available';
						isStringList = true;
					}

					return (
						<HubTooltip
							key={`error-${tag.avbId}`}
							title={
								<div css={styles.errorTooltipContainer}>
									{isStringList ? (
										<ol css={styles.errorTooltipList}>
											{errorTooltip.split('\n').map((error, index) => (
												<li key={index}>{error}</li>
											))}
										</ol>
									) : (
										<pre css={styles.errorTooltipPre}>{errorTooltip}</pre>
									)}
								</div>
							}
							styles={{ tooltip: { maxWidth: 500 } }}
						>
							<div>
								<Tag
									textStyles={textStyles}
									text={tag.avbId.toLocaleUpperCase()}
									size="tiny"
									color={theme.colors.statuses.DELETED}
								/>
							</div>
						</HubTooltip>
					);
				}
			})}
		/>
	);
}

/**
 * Below is a modified version of 'react-truncate-list'
 * optimized for the upload result tags cell to be used in data tables.
 */

type RectMeasurements = Pick<DOMRect, 'bottom' | 'top' | 'left' | 'right'>;
const rectContainsRect = (
	parent: RectMeasurements,
	child: RectMeasurements
) => {
	return (
		child.top >= parent.top &&
		child.bottom <= parent.bottom &&
		child.left >= parent.left &&
		child.right <= parent.right
	);
};

type RenderTruncator = ({
	hiddenItemsCount,
}: {
	hiddenItemsCount: number;
}) => ReactNode;

export interface TruncatedListProps extends Omit<
	ComponentPropsWithoutRef<'ul'>,
	'children'
> {
	renderTruncator: RenderTruncator;
	items: ReactNode[];
	truncatorWidth: number;
	alwaysShowTruncator?: boolean;
}

function TruncateEffect({
	containerRef,
	alwaysShowTruncator,
	truncatorWidth,
}: {
	containerRef: RefObject<HTMLUListElement>;
	truncatorWidth: number;
	alwaysShowTruncator?: boolean;
}) {
	const initialized = useRef(false);
	useEffect(() => {
		const truncate = () => {
			if (!containerRef.current) {
				return;
			}
			// eslint-disable-next-line unicorn/prefer-spread
			const childNodes = Array.from(
				containerRef.current.children
			) as HTMLElement[];

			// If there are no items (the last truncator is always included).
			if (childNodes.length === 1) {
				return;
			}

			if (!containerRef.current) {
				return;
			}
			// If being called a second time, then we need
			// to mark all items as shown to recalculate
			if (initialized.current) {
				for (let i = childNodes.length - 2; i >= 1; i -= 2) {
					const itemEl = childNodes[i];
					const truncatorEl = childNodes[i - 1];
					if (truncatorEl) truncatorEl.hidden = true;
					if (itemEl) itemEl.hidden = false;
				}
			} else {
				initialized.current = true;
			}

			//
			// Test if truncation is necessary.
			//
			if (alwaysShowTruncator) {
				// if the last truncator fits, exit
				const truncatorEl = childNodes.at(-1);
				if (truncatorEl) {
					truncatorEl.hidden = false;

					if (
						rectContainsRect(
							containerRef.current.getBoundingClientRect(),
							truncatorEl.getBoundingClientRect()
						)
					) {
						return;
					}
					truncatorEl.hidden = true;
				}
			} else {
				// if the last item fits, exit
				const itemEl = childNodes.at(-2);

				if (
					itemEl &&
					rectContainsRect(
						containerRef.current.getBoundingClientRect(),
						itemEl.getBoundingClientRect()
					)
				) {
					return;
				}
			}

			const containerRect = containerRef.current.getBoundingClientRect();
			function markItemsAsHidden(i: number) {
				const truncatorEl = childNodes[i - 1];
				if (truncatorEl) truncatorEl.hidden = false;
				for (const j of _.range(i, childNodes.length, 2)) {
					const node = childNodes[j];
					if (node) node.hidden = true;
				}
			}

			//
			// Go backward and find the last item that can fit.
			//
			for (let i = childNodes.length - 2; i >= 1; i -= 2) {
				const itemEl = childNodes[i];
				if (!itemEl) continue;

				const itemRect = itemEl.getBoundingClientRect();
				const containsWithRoomRightForTruncator = rectContainsRect(
					{
						top: containerRect.top,
						bottom: containerRect.bottom,
						left: containerRect.left,
						right: containerRect.right - truncatorWidth,
					},
					itemRect
				);

				const containsWithRoomBelowForTruncator = rectContainsRect(
					containerRect,
					{
						top: itemRect.top,
						// assume truncator is same height as item
						bottom: itemRect.bottom + itemRect.height,
						left: itemRect.left,
						right: itemRect.right,
					}
				);

				if (
					containsWithRoomRightForTruncator ||
					containsWithRoomBelowForTruncator
				) {
					markItemsAsHidden(i + 2);
					return;
				}
			}

			// none fit, so mark all items hidden
			markItemsAsHidden(1);
		};

		const truncateDebounce = _.debounce(truncate, 50);

		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				// Use entry to avoid unused variable warning
				void entry;
				truncateDebounce();
			}
		});

		if (containerRef.current) {
			resizeObserver.observe(containerRef.current);
		}

		return () => {
			resizeObserver.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [alwaysShowTruncator, truncatorWidth]);

	return null;
}

export function TruncatedList({
	renderTruncator,
	alwaysShowTruncator,
	items,
	truncatorWidth,
	...ulProps
}: TruncatedListProps) {
	const containerRef = useRef<HTMLUListElement>(null);

	const itemsWithTruncator = items.map((item, i) => (
		<Fragment key={i}>
			<li hidden>{renderTruncator({ hiddenItemsCount: items.length - i })}</li>
			<li css={{ maxWidth: `calc(100% - ${truncatorWidth}px)` }}>{item}</li>
		</Fragment>
	));

	return (
		<>
			<TruncateEffect
				containerRef={containerRef}
				alwaysShowTruncator={alwaysShowTruncator}
				truncatorWidth={truncatorWidth}
			/>

			<ul ref={containerRef} {...ulProps}>
				{itemsWithTruncator}

				<li hidden>{renderTruncator({ hiddenItemsCount: 0 })}</li>
			</ul>
		</>
	);
}
